import { addDays, parseISO } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { loadDemoDatabase, saveDemoDatabase, type DemoDatabase } from '@/data/demoData'
import { isDemoMode, requireSupabase } from '@/lib/supabase'
import { analyzePeriodRange, enumeratePeriodDates, reconcilePeriodItems } from '@/services/periodRange'
import type {
  AppProfile,
  EmployeeInput,
  EmployeeRestriction,
  Lookup,
  Meal,
  MealInput,
  MealTag,
  PeriodRangeImpact,
  Selection,
  SelectionInput,
  SystemSettings,
  WeekBundle,
  WeekDay,
  WeekDayInput,
  WeekInput,
  WeekParticipant,
  WeekStatus,
  WeekUpdateOptions,
} from '@/types'
import { APP_TIMEZONE, deadlinePassed, localDateAtNoon } from '@/utils'

export interface AppSnapshot {
  weeks: WeekBundle[]
  meals: Meal[]
  employees: AppProfile[]
  selections: Selection[]
  weekParticipants: WeekParticipant[]
  sectors: Lookup[]
  shifts: Lookup[]
  locations: Lookup[]
  settings: SystemSettings
}

const DEFAULT_SETTINGS: SystemSettings = {
  companyName: 'Mi empresa',
  systemName: 'Buen Plato',
  welcomeText: 'Elegí tus viandas del período de forma simple y rápida.',
  logoUrl: null,
  serviceWeekdays: [1, 2, 3, 4, 5],
  timezone: APP_TIMEZONE,
}

const isoNow = () => new Date().toISOString()
const newId = () => crypto.randomUUID()
const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' ? value as Record<string, unknown> : {}
const stringOrNull = (value: unknown): string | null => typeof value === 'string' ? value : null
const stringValue = (value: unknown, fallback = ''): string => typeof value === 'string' ? value : fallback
const booleanValue = (value: unknown, fallback = false): boolean => typeof value === 'boolean' ? value : fallback
const numberValue = (value: unknown, fallback = 0): number => typeof value === 'number' ? value : fallback
const arrayValue = (value: unknown): unknown[] => Array.isArray(value) ? value : []

const SNAPSHOT_PAGE_SIZE = 1_000

interface RowPage {
  data: unknown[] | null
  error: unknown | null
  count: number | null
}

type RowPageFetcher = (from: number, to: number, includeCount: boolean) => PromiseLike<RowPage>

async function fetchAllRows(
  label: string,
  fetchPage: RowPageFetcher,
): Promise<{ data: unknown[] | null; error: unknown | null }> {
  const rows: unknown[] = []
  let expectedCount: number | null = null

  while (true) {
    const response = await fetchPage(
      rows.length,
      rows.length + SNAPSHOT_PAGE_SIZE - 1,
      expectedCount === null,
    )
    const { data, error, count } = response
    if (error) return { data: null, error }
    if (expectedCount === null && count !== null) expectedCount = count

    const page = arrayValue(data)
    if (page.length === 0) {
      if (expectedCount !== null && rows.length < expectedCount) {
        return {
          data: null,
          error: new Error(`La consulta de ${label} se interrumpió antes de recuperar todas las filas.`),
        }
      }
      return { data: rows, error: null }
    }

    rows.push(...page)
    if (expectedCount !== null ? rows.length >= expectedCount : page.length < SNAPSHOT_PAGE_SIZE) {
      return { data: rows, error: null }
    }
  }
}

function mapTag(value: unknown): MealTag {
  const row = record(value)
  return { id: stringValue(row.id), name: stringValue(row.name), color: stringOrNull(row.color) }
}

function mapMeal(value: unknown): Meal {
  const row = record(value)
  const directTags = arrayValue(row.tags)
  const relationTags = arrayValue(row.meal_tag_relations).map((relation) => record(relation).tag)
  return {
    id: stringValue(row.id),
    name: stringValue(row.name),
    description: stringOrNull(row.description),
    imageUrl: stringOrNull(row.image_url ?? row.imageUrl),
    active: booleanValue(row.active, true),
    tags: (directTags.length ? directTags : relationTags).map(mapTag).filter((tag) => tag.id),
    createdAt: stringValue(row.created_at ?? row.createdAt, isoNow()),
    updatedAt: stringValue(row.updated_at ?? row.updatedAt, isoNow()),
  }
}

function mapWeekDay(value: unknown): WeekDay {
  const row = record(value)
  const dayMeals = arrayValue(row.day_meals ?? row.dayMeals)
    .map((entry) => {
      const dayMeal = record(entry)
      return {
        id: stringValue(dayMeal.id),
        weekDayId: stringValue(dayMeal.week_day_id ?? dayMeal.weekDayId),
        mealId: stringValue(dayMeal.meal_id ?? dayMeal.mealId),
        displayOrder: numberValue(dayMeal.display_order ?? dayMeal.displayOrder),
        active: booleanValue(dayMeal.active, true),
        meal: mapMeal(dayMeal.meal),
        mealNameSnapshot: stringOrNull(dayMeal.meal_name_snapshot ?? dayMeal.mealNameSnapshot),
        mealDescriptionSnapshot: stringOrNull(dayMeal.meal_description_snapshot ?? dayMeal.mealDescriptionSnapshot),
        mealImageUrlSnapshot: stringOrNull(dayMeal.meal_image_url_snapshot ?? dayMeal.mealImageUrlSnapshot),
      }
    })
    .sort((a, b) => a.displayOrder - b.displayOrder)
  return {
    id: stringValue(row.id),
    weekId: stringValue(row.week_id ?? row.weekId),
    date: stringValue(row.date),
    serviceEnabled: booleanValue(row.service_enabled ?? row.serviceEnabled, true),
    note: stringOrNull(row.note),
    dayMeals,
  }
}

function mapWeek(value: unknown): WeekBundle {
  const row = record(value)
  return {
    id: stringValue(row.id),
    name: stringOrNull(row.name),
    startDate: stringValue(row.start_date ?? row.startDate),
    endDate: stringValue(row.end_date ?? row.endDate),
    selectionDeadline: stringValue(row.selection_deadline ?? row.selectionDeadline),
    status: stringValue(row.status, 'draft') as WeekStatus,
    createdBy: stringOrNull(row.created_by ?? row.createdBy),
    days: arrayValue(row.days ?? row.week_days).map(mapWeekDay).sort((a, b) => a.date.localeCompare(b.date)),
    createdAt: stringValue(row.created_at ?? row.createdAt, isoNow()),
    updatedAt: stringValue(row.updated_at ?? row.updatedAt, isoNow()),
  }
}

function mapRestriction(value: unknown): EmployeeRestriction {
  const row = record(value)
  return {
    id: stringValue(row.id),
    employeeId: stringValue(row.employee_id ?? row.employeeId),
    type: stringValue(row.type, 'other') as EmployeeRestriction['type'],
    notes: stringOrNull(row.notes),
  }
}

export function mapProfile(value: unknown): AppProfile {
  const row = record(value)
  return {
    id: stringValue(row.id),
    firstName: stringValue(row.first_name ?? row.firstName),
    lastName: stringValue(row.last_name ?? row.lastName),
    username: stringValue(row.username),
    email: stringOrNull(row.email),
    role: stringValue(row.role, 'employee') as AppProfile['role'],
    employeeNumber: stringOrNull(row.employee_number ?? row.employeeNumber),
    sectorId: stringOrNull(row.sector_id ?? row.sectorId),
    shiftId: stringOrNull(row.shift_id ?? row.shiftId),
    locationId: stringOrNull(row.location_id ?? row.locationId),
    active: booleanValue(row.active, true),
    lastSelectionAt: stringOrNull(row.last_selection_at ?? row.lastSelectionAt),
    avatarUrl: stringOrNull(row.avatar_url ?? row.avatarUrl),
    restrictions: arrayValue(row.restrictions ?? row.employee_restrictions).map(mapRestriction),
    createdAt: stringValue(row.created_at ?? row.createdAt, isoNow()),
    updatedAt: stringValue(row.updated_at ?? row.updatedAt, isoNow()),
  }
}

function mapSelection(value: unknown): Selection {
  const row = record(value)
  return {
    id: stringValue(row.id),
    employeeId: stringValue(row.employee_id ?? row.employeeId),
    weekId: stringValue(row.week_id ?? row.weekId),
    weekDayId: stringValue(row.week_day_id ?? row.weekDayId),
    mealId: stringOrNull(row.meal_id ?? row.mealId),
    noMeal: booleanValue(row.no_meal ?? row.noMeal),
    confirmed: booleanValue(row.confirmed),
    modifiedByAdmin: stringOrNull(row.modified_by_admin ?? row.modifiedByAdmin),
    modifiedAt: stringOrNull(row.modified_at ?? row.modifiedAt),
    createdAt: stringValue(row.created_at ?? row.createdAt, isoNow()),
    updatedAt: stringValue(row.updated_at ?? row.updatedAt, isoNow()),
  }
}

function mapWeekParticipant(value: unknown): WeekParticipant {
  const row = record(value)
  return {
    weekId: stringValue(row.week_id ?? row.weekId),
    employeeId: stringValue(row.employee_id ?? row.employeeId),
    required: booleanValue(row.required, true),
  }
}

function mapLookup(value: unknown): Lookup {
  const row = record(value)
  return { id: stringValue(row.id), name: stringValue(row.name), active: booleanValue(row.active, true) }
}

function mapSettings(value: unknown): SystemSettings {
  const row = record(value)
  const weekdays = arrayValue(row.service_weekdays ?? row.serviceWeekdays).filter((day): day is number => typeof day === 'number')
  return {
    companyName: stringValue(row.company_name ?? row.companyName, DEFAULT_SETTINGS.companyName),
    systemName: stringValue(row.system_name ?? row.systemName, DEFAULT_SETTINGS.systemName),
    welcomeText: stringValue(row.welcome_text ?? row.welcomeText, DEFAULT_SETTINGS.welcomeText),
    logoUrl: stringOrNull(row.logo_url ?? row.logoUrl),
    serviceWeekdays: weekdays.length ? weekdays : DEFAULT_SETTINGS.serviceWeekdays,
    timezone: stringValue(row.timezone, APP_TIMEZONE),
  }
}

function getDemo(): DemoDatabase {
  return loadDemoDatabase()
}

function commitDemo(database: DemoDatabase): void {
  saveDemoDatabase(database)
}

function demoAudit(database: DemoDatabase, userId: string | null, action: string, entityType: string, entityId: string | null, details: Record<string, unknown> = {}): void {
  database.auditLogs.unshift({ id: newId(), userId, action, entityType, entityId, details, createdAt: isoNow() })
}

function dateOnly(value: Date): string {
  return formatInTimeZone(value, APP_TIMEZONE, 'yyyy-MM-dd')
}

function cloneSnapshot(database: DemoDatabase, profile: AppProfile): AppSnapshot {
  const isAdmin = profile.role === 'admin'
  const weeks = database.weeks
    .filter((week) => isAdmin || week.status !== 'draft')
    .sort((a, b) => b.startDate.localeCompare(a.startDate))
  const weekParticipants = weeks.flatMap((week) => {
    const selectedEmployeeIds = new Set(
      database.selections
        .filter((selection) => selection.weekId === week.id)
        .map((selection) => selection.employeeId),
    )
    return database.profiles
      .filter((employee) => employee.role === 'employee')
      .filter((employee) => week.status === 'open' ? employee.active : employee.active || selectedEmployeeIds.has(employee.id))
      .map((employee) => ({ weekId: week.id, employeeId: employee.id, required: true }))
  })
  return structuredClone({
    weeks,
    meals: database.meals,
    employees: isAdmin ? database.profiles.filter((item) => item.role === 'employee') : [profile],
    selections: isAdmin ? database.selections : database.selections.filter((selection) => selection.employeeId === profile.id),
    weekParticipants: isAdmin
      ? weekParticipants
      : weekParticipants.filter((participant) => participant.employeeId === profile.id),
    sectors: database.sectors,
    shifts: database.shifts,
    locations: database.locations,
    settings: database.settings,
  })
}

export async function fetchAppSnapshot(profile: AppProfile): Promise<AppSnapshot> {
  if (isDemoMode) {
    const database = getDemo()
    let changed = false
    database.weeks.forEach((week) => {
      if (week.status === 'open' && deadlinePassed(week)) {
        week.status = 'closed'
        week.updatedAt = isoNow()
        changed = true
      }
    })
    if (changed) commitDemo(database)
    return cloneSnapshot(database, profile)
  }
  const client = requireSupabase()
  const { error: deadlineError } = await client.rpc('close_expired_weeks')
  if (deadlineError) throw deadlineError
  const [weeksResult, mealsResult, selectionsResult, participantsResult, sectorsResult, shiftsResult, locationsResult, settingsResult, employeesResult] = await Promise.all([
    client.from('weeks').select('*, days:week_days(*, day_meals(*, meal:meals(*, meal_tag_relations(tag:meal_tags(*)))))').order('start_date', { ascending: false }),
    client.from('meals').select('*, meal_tag_relations(tag:meal_tags(*))').order('name'),
    fetchAllRows('selections', (from, to, includeCount) => client
      .from('selections')
      .select('*', { count: includeCount ? 'exact' : undefined })
      .order('id', { ascending: true })
      .range(from, to)),
    fetchAllRows('week_employees', (from, to, includeCount) => client
      .from('week_employees')
      .select('week_id, employee_id, required', { count: includeCount ? 'exact' : undefined })
      .order('week_id', { ascending: true })
      .order('employee_id', { ascending: true })
      .range(from, to)),
    client.from('sectors').select('*').order('name'),
    client.from('shifts').select('*').order('name'),
    client.from('locations').select('*').order('name'),
    client.from('system_settings').select('*').eq('id', 'default').maybeSingle(),
    profile.role === 'admin'
      ? client.from('profiles').select('*, employee_restrictions(*)').eq('role', 'employee').order('last_name')
      : Promise.resolve({ data: [profile], error: null }),
  ])
  const error = weeksResult.error ?? mealsResult.error ?? selectionsResult.error ?? participantsResult.error ?? sectorsResult.error ?? shiftsResult.error ?? locationsResult.error ?? settingsResult.error ?? employeesResult.error
  if (error) throw error
  return {
    weeks: arrayValue(weeksResult.data).map(mapWeek),
    meals: arrayValue(mealsResult.data).map(mapMeal),
    selections: arrayValue(selectionsResult.data).map(mapSelection),
    weekParticipants: arrayValue(participantsResult.data).map(mapWeekParticipant),
    sectors: arrayValue(sectorsResult.data).map(mapLookup),
    shifts: arrayValue(shiftsResult.data).map(mapLookup),
    locations: arrayValue(locationsResult.data).map(mapLookup),
    settings: settingsResult.data ? mapSettings(settingsResult.data) : DEFAULT_SETTINGS,
    employees: arrayValue(employeesResult.data).map(mapProfile),
  }
}

export async function fetchPublicSettings(): Promise<SystemSettings> {
  if (isDemoMode) return structuredClone(getDemo().settings)
  const { data, error } = await requireSupabase().from('system_settings').select('*').eq('id', 'default').maybeSingle()
  if (error) throw error
  return data ? mapSettings(data) : DEFAULT_SETTINGS
}

export async function createWeek(input: WeekInput, actorId: string): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const id = newId()
    const days: WeekDay[] = enumeratePeriodDates(input.startDate, input.endDate).map((date) => {
      const cursor = localDateAtNoon(date)
      const weekday = Number(formatInTimeZone(cursor, APP_TIMEZONE, 'i'))
      return {
        id: newId(),
        weekId: id,
        date,
        serviceEnabled: database.settings.serviceWeekdays.includes(weekday),
        note: null,
        dayMeals: [],
      }
    })
    database.weeks.unshift({
      id,
      name: input.name ?? null,
      startDate: input.startDate,
      endDate: input.endDate,
      selectionDeadline: input.selectionDeadline,
      status: input.status ?? 'draft',
      createdBy: actorId,
      days,
      createdAt: isoNow(),
      updatedAt: isoNow(),
    })
    demoAudit(database, actorId, 'week.created', 'week', id)
    commitDemo(database)
    return
  }
  const { error } = await requireSupabase().from('weeks').insert({
    name: input.name ?? null,
    start_date: input.startDate,
    end_date: input.endDate,
    selection_deadline: input.selectionDeadline,
    status: input.status ?? 'draft',
    created_by: actorId,
  })
  if (error) throw error
}

function rangeImpactForDemo(
  database: DemoDatabase,
  week: WeekBundle,
  startDate: string,
  endDate: string,
): PeriodRangeImpact {
  return analyzePeriodRange(
    week.days,
    database.selections.filter((selection) => selection.weekId === week.id),
    startDate,
    endDate,
  )
}

function mapPeriodRangeImpact(value: unknown): PeriodRangeImpact {
  const row = record(Array.isArray(value) ? value[0] : value)
  return {
    removedDayCount: numberValue(row.removed_day_count ?? row.removedDayCount),
    affectedDayCount: numberValue(
      row.affected_day_count ?? row.days_with_information ?? row.affectedDayCount,
    ),
    menuOptionCount: numberValue(row.menu_option_count ?? row.menuOptionCount),
    selectionCount: numberValue(row.selection_count ?? row.selectionCount),
    confirmedSelectionCount: numberValue(
      row.confirmed_selection_count ?? row.confirmedSelectionCount,
    ),
    removedDates: arrayValue(row.removed_dates ?? row.removedDates).filter(
      (date): date is string => typeof date === 'string',
    ),
  }
}

export async function previewWeekRange(
  id: string,
  startDate: string,
  endDate: string,
): Promise<PeriodRangeImpact> {
  if (endDate < startDate) {
    throw new Error('La fecha final debe ser igual o posterior a la fecha inicial.')
  }
  if (isDemoMode) {
    const database = getDemo()
    const week = database.weeks.find((item) => item.id === id)
    if (!week) throw new Error('Período no encontrado.')
    return rangeImpactForDemo(database, week, startDate, endDate)
  }
  const { data, error } = await requireSupabase().rpc('get_period_range_change_impact', {
    target_week_id: id,
    new_start_date: startDate,
    new_end_date: endDate,
  })
  if (error) throw error
  return mapPeriodRangeImpact(data)
}

export async function updateWeek(
  id: string,
  patch: Partial<WeekInput>,
  options: WeekUpdateOptions = {},
): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const week = database.weeks.find((item) => item.id === id)
    if (!week) throw new Error('Período no encontrado.')
    const nextStartDate = patch.startDate ?? week.startDate
    const nextEndDate = patch.endDate ?? week.endDate
    if (nextEndDate < nextStartDate) {
      throw new Error('La fecha final debe ser igual o posterior a la fecha inicial.')
    }
    const datesChanged = nextStartDate !== week.startDate || nextEndDate !== week.endDate
    if (datesChanged && week.status === 'finished') {
      throw new Error('El período finalizado debe habilitarse para edición antes de cambiar sus fechas.')
    }
    if (datesChanged) {
      const impact = rangeImpactForDemo(database, week, nextStartDate, nextEndDate)
      if (impact.affectedDayCount > 0 && !options.confirmDataLoss) {
        const error = new Error('El nuevo rango elimina días que ya tienen información cargada.')
        Object.assign(error, { code: 'PERIOD_RANGE_CONFIRMATION_REQUIRED', impact })
        throw error
      }
      const removedDayIds = new Set(
        week.days
          .filter((day) => day.date < nextStartDate || day.date > nextEndDate)
          .map((day) => day.id),
      )
      if (removedDayIds.size > 0) {
        database.selections = database.selections.filter(
          (selection) => selection.weekId !== week.id || !removedDayIds.has(selection.weekDayId),
        )
      }
      const { items: nextDays } = reconcilePeriodItems(
        week.days,
        nextStartDate,
        nextEndDate,
        (date): WeekDay => {
          const cursor = localDateAtNoon(date)
          const weekday = Number(formatInTimeZone(cursor, APP_TIMEZONE, 'i'))
          return {
            id: newId(),
            weekId: week.id,
            date,
            serviceEnabled: database.settings.serviceWeekdays.includes(weekday),
            note: null,
            dayMeals: [],
          }
        },
      )
      week.days = nextDays
      demoAudit(database, null, 'period.range_updated', 'week', week.id, {
        previousStartDate: week.startDate,
        previousEndDate: week.endDate,
        nextStartDate,
        nextEndDate,
        ...impact,
      })
    }
    Object.assign(week, {
      ...(patch.name !== undefined ? { name: patch.name } : {}),
      startDate: nextStartDate,
      endDate: nextEndDate,
      ...(patch.selectionDeadline ? { selectionDeadline: patch.selectionDeadline } : {}),
      updatedAt: isoNow(),
    })
    commitDemo(database)
    return
  }
  const client = requireSupabase()
  if (patch.startDate !== undefined || patch.endDate !== undefined) {
    const { data: current, error: currentError } = await client
      .from('weeks')
      .select('start_date, end_date, name, selection_deadline')
      .eq('id', id)
      .single()
    if (currentError) throw currentError
    const currentRow = record(current)
    const { error } = await client.rpc('update_service_period', {
      target_week_id: id,
      new_start_date: patch.startDate ?? stringValue(currentRow.start_date),
      new_end_date: patch.endDate ?? stringValue(currentRow.end_date),
      new_name: patch.name !== undefined ? patch.name : stringOrNull(currentRow.name),
      new_selection_deadline: patch.selectionDeadline ?? stringValue(currentRow.selection_deadline),
      confirm_data_loss: options.confirmDataLoss ?? false,
    })
    if (error) throw error
    return
  }
  const payload: Record<string, unknown> = {}
  if (patch.name !== undefined) payload.name = patch.name
  if (patch.selectionDeadline) payload.selection_deadline = patch.selectionDeadline
  const { error } = await client.from('weeks').update(payload).eq('id', id)
  if (error) throw error
}

export async function deleteWeek(id: string): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const week = database.weeks.find((item) => item.id === id)
    if (!week || week.status !== 'draft') throw new Error('Solamente se pueden eliminar períodos en borrador.')
    database.weeks = database.weeks.filter((item) => item.id !== id)
    database.selections = database.selections.filter((selection) => selection.weekId !== id)
    commitDemo(database)
    return
  }
  const { error } = await requireSupabase().from('weeks').delete().eq('id', id).eq('status', 'draft')
  if (error) throw error
}

export async function duplicateWeek(id: string, actorId: string): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const source = database.weeks.find((item) => item.id === id)
    if (!source) throw new Error('Período no encontrado.')
    const newWeekId = newId()
    const nextStart = addDays(localDateAtNoon(source.endDate), 1)
    const dayOffset = Math.round((nextStart.getTime() - localDateAtNoon(source.startDate).getTime()) / 86_400_000)
    const days = source.days.map((day) => {
      const dayId = newId()
      return {
        ...structuredClone(day),
        id: dayId,
        weekId: newWeekId,
        date: dateOnly(addDays(localDateAtNoon(day.date), dayOffset)),
        dayMeals: day.dayMeals.map((dayMeal) => ({ ...structuredClone(dayMeal), id: newId(), weekDayId: dayId })),
      }
    })
    const deadline = fromZonedTime(`${dateOnly(addDays(nextStart, -1))}T18:00:00`, APP_TIMEZONE).toISOString()
    database.weeks.unshift({
      ...structuredClone(source),
      id: newWeekId,
      name: source.name ? `${source.name} (copia)` : 'Nuevo período',
      startDate: days.at(0)?.date ?? source.startDate,
      endDate: days.at(-1)?.date ?? source.endDate,
      selectionDeadline: deadline,
      status: 'draft',
      createdBy: actorId,
      days,
      createdAt: isoNow(),
      updatedAt: isoNow(),
    })
    demoAudit(database, actorId, 'week.duplicated', 'week', newWeekId, { sourceId: id })
    commitDemo(database)
    return
  }
  const { error } = await requireSupabase().rpc('duplicate_week', { source_week_id: id })
  if (error) throw error
}

export async function transitionWeek(id: string, status: WeekStatus, actorId: string): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const week = database.weeks.find((item) => item.id === id)
    if (!week) throw new Error('Período no encontrado.')
    if (status === 'open') {
      database.weeks.filter((item) => item.id !== id && item.status === 'open').forEach((item) => { item.status = 'closed' })
      if (deadlinePassed(week)) week.selectionDeadline = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    }
    week.status = status
    week.updatedAt = isoNow()
    demoAudit(database, actorId, `week.${status}`, 'week', id)
    commitDemo(database)
    return
  }
  const { error } = await requireSupabase().rpc('transition_week_status', { target_week_id: id, new_status: status })
  if (error) throw error
}

export async function updateDay(id: string, patch: Partial<WeekDayInput>): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const day = database.weeks.flatMap((week) => week.days).find((item) => item.id === id)
    if (!day) throw new Error('Día no encontrado.')
    if (patch.date) day.date = patch.date
    if (patch.serviceEnabled !== undefined) day.serviceEnabled = patch.serviceEnabled
    if (patch.note !== undefined) day.note = patch.note ?? null
    commitDemo(database)
    return
  }
  const payload: Record<string, unknown> = {}
  if (patch.date) payload.date = patch.date
  if (patch.serviceEnabled !== undefined) payload.service_enabled = patch.serviceEnabled
  if (patch.note !== undefined) payload.note = patch.note
  const { error } = await requireSupabase().from('week_days').update(payload).eq('id', id)
  if (error) throw error
}

export async function assignMeal(dayId: string, mealId: string): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const day = database.weeks.flatMap((week) => week.days).find((item) => item.id === dayId)
    const meal = database.meals.find((item) => item.id === mealId)
    if (!day || !meal) throw new Error('Día o plato no encontrado.')
    if (!day.dayMeals.some((item) => item.mealId === mealId)) day.dayMeals.push({
      id: newId(),
      weekDayId: dayId,
      mealId,
      displayOrder: day.dayMeals.length,
      active: true,
      meal: structuredClone(meal),
      mealNameSnapshot: meal.name,
      mealDescriptionSnapshot: meal.description,
      mealImageUrlSnapshot: meal.imageUrl,
    })
    commitDemo(database)
    return
  }
  const client = requireSupabase()
  const { count, error: countError } = await client.from('day_meals').select('*', { count: 'exact', head: true }).eq('week_day_id', dayId)
  if (countError) throw countError
  const { error } = await client.from('day_meals').insert({ week_day_id: dayId, meal_id: mealId, display_order: count ?? 0 })
  if (error) throw error
}

export async function unassignMeal(dayMealId: string): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    database.weeks.forEach((week) => week.days.forEach((day) => { day.dayMeals = day.dayMeals.filter((item) => item.id !== dayMealId) }))
    commitDemo(database)
    return
  }
  const { error } = await requireSupabase().from('day_meals').delete().eq('id', dayMealId)
  if (error) throw error
}

export async function reorderDayMeals(dayId: string, orderedDayMealIds: string[]): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const day = database.weeks.flatMap((week) => week.days).find((item) => item.id === dayId)
    if (!day) return
    day.dayMeals.sort((a, b) => orderedDayMealIds.indexOf(a.id) - orderedDayMealIds.indexOf(b.id))
    day.dayMeals.forEach((item, index) => { item.displayOrder = index })
    commitDemo(database)
    return
  }
  const client = requireSupabase()
  const results = await Promise.all(orderedDayMealIds.map((id, index) => client.from('day_meals').update({ display_order: index }).eq('id', id)))
  const error = results.find((result) => result.error)?.error
  if (error) throw error
}

async function syncMealTags(mealId: string, tagIds: string[]): Promise<void> {
  const client = requireSupabase()
  const { error: deleteError } = await client.from('meal_tag_relations').delete().eq('meal_id', mealId)
  if (deleteError) throw deleteError
  if (!tagIds.length) return
  const { error } = await client.from('meal_tag_relations').insert(tagIds.map((tagId) => ({ meal_id: mealId, tag_id: tagId })))
  if (error) throw error
}

async function resolveMealTagIds(input: Pick<MealInput, 'tagIds' | 'tags'>): Promise<string[]> {
  const ids = new Set(input.tagIds ?? [])
  const newNames: string[] = []
  for (const tag of input.tags ?? []) {
    if (tag.id && !tag.id.startsWith('new:')) ids.add(tag.id)
    else if (tag.name.trim()) newNames.push(tag.name.trim())
  }
  if (newNames.length) {
    const { data, error } = await requireSupabase()
      .from('meal_tags')
      .upsert([...new Set(newNames)].map((name) => ({ name, active: true })), { onConflict: 'name' })
      .select('id')
    if (error) throw error
    arrayValue(data).forEach((row) => ids.add(stringValue(record(row).id)))
  }
  return [...ids].filter(Boolean)
}

export async function createMeal(input: MealInput): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const id = newId()
    const tags = input.tags ?? database.tags.filter((tag) => input.tagIds?.includes(tag.id))
    database.meals.push({ id, name: input.name, description: input.description ?? null, imageUrl: input.imageUrl ?? null, active: input.active ?? true, tags, createdAt: isoNow(), updatedAt: isoNow() })
    commitDemo(database)
    return
  }
  const client = requireSupabase()
  const { data, error } = await client.from('meals').insert({ name: input.name, description: input.description ?? null, image_url: input.imageUrl ?? null, active: input.active ?? true }).select('id').single()
  if (error) throw error
  const mealId = stringValue(record(data).id)
  const tagIds = await resolveMealTagIds(input)
  if (tagIds.length) await syncMealTags(mealId, tagIds)
}

export async function updateMeal(id: string, patch: Partial<MealInput>): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const meal = database.meals.find((item) => item.id === id)
    if (!meal) throw new Error('Plato no encontrado.')
    if (patch.name !== undefined) meal.name = patch.name
    if (patch.description !== undefined) meal.description = patch.description ?? null
    if (patch.imageUrl !== undefined) meal.imageUrl = patch.imageUrl ?? null
    if (patch.active !== undefined) meal.active = patch.active
    if (patch.tags) meal.tags = patch.tags
    if (patch.tagIds) meal.tags = database.tags.filter((tag) => patch.tagIds?.includes(tag.id))
    meal.updatedAt = isoNow()
    database.weeks.forEach((week) => week.days.forEach((day) => day.dayMeals.forEach((dayMeal) => { if (dayMeal.mealId === id) dayMeal.meal = structuredClone(meal) })))
    commitDemo(database)
    return
  }
  const payload: Record<string, unknown> = {}
  if (patch.name !== undefined) payload.name = patch.name
  if (patch.description !== undefined) payload.description = patch.description
  if (patch.imageUrl !== undefined) payload.image_url = patch.imageUrl
  if (patch.active !== undefined) payload.active = patch.active
  const { error } = await requireSupabase().from('meals').update(payload).eq('id', id)
  if (error) throw error
  if (patch.tagIds || patch.tags) await syncMealTags(id, await resolveMealTagIds(patch))
}

export async function archiveMeal(id: string): Promise<void> {
  return updateMeal(id, { active: false })
}

const allowedImageTypes = new Set(['image/jpeg', 'image/png', 'image/webp'])

async function compressImage(file: File): Promise<Blob> {
  if (!allowedImageTypes.has(file.type)) throw new Error('Usá una imagen JPG, PNG o WEBP.')
  if (file.size > 8 * 1024 * 1024) throw new Error('La imagen no puede superar los 8 MB.')
  const bitmap = await createImageBitmap(file)
  const maxDimension = 1600
  const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(bitmap.width * scale))
  canvas.height = Math.max(1, Math.round(bitmap.height * scale))
  const context = canvas.getContext('2d')
  if (!context) throw new Error('No pudimos procesar la imagen.')
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()
  return new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('No pudimos comprimir la imagen.')), 'image/webp', .82))
}

export async function uploadMealImage(file: File, actorId: string): Promise<string> {
  const blob = await compressImage(file)
  if (isDemoMode) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = () => reject(new Error('No pudimos leer la imagen.'))
      reader.readAsDataURL(blob)
    })
  }
  const client = requireSupabase()
  const path = `${actorId}/${new Date().getUTCFullYear()}/${newId()}.webp`
  const { error } = await client.storage.from('meal-images').upload(path, blob, { contentType: 'image/webp', cacheControl: '31536000', upsert: false })
  if (error) throw error
  return client.storage.from('meal-images').getPublicUrl(path).data.publicUrl
}

export async function createEmployee(input: EmployeeInput, actorId: string): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    if (database.profiles.some((item) => item.username.toLowerCase() === input.username.toLowerCase())) throw new Error('duplicate username')
    const id = newId()
    const email = input.email ?? `${input.username}@empresa.local`
    const created: AppProfile = {
      id,
      firstName: input.firstName,
      lastName: input.lastName,
      username: input.username,
      email,
      role: input.role ?? 'employee',
      employeeNumber: input.employeeNumber ?? null,
      sectorId: input.sectorId ?? null,
      shiftId: input.shiftId ?? null,
      locationId: input.locationId ?? null,
      active: input.active ?? true,
      lastSelectionAt: null,
      restrictions: (input.restrictions ?? []).map((restriction) => ({ id: newId(), employeeId: id, ...restriction })),
      createdAt: isoNow(),
      updatedAt: isoNow(),
    }
    database.profiles.push(created)
    database.credentials[input.username.toLowerCase()] = input.password ?? input.pin ?? '1234'
    database.credentials[email.toLowerCase()] = input.password ?? input.pin ?? '1234'
    demoAudit(database, actorId, 'employee.created', 'profile', id)
    commitDemo(database)
    return
  }
  const { error } = await requireSupabase().functions.invoke('admin-manage-user', { body: { action: 'create', employee: input } })
  if (error) throw error
}

function profilePayload(patch: Partial<EmployeeInput>): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  if (patch.firstName !== undefined) payload.first_name = patch.firstName
  if (patch.lastName !== undefined) payload.last_name = patch.lastName
  if (patch.username !== undefined) payload.username = patch.username
  if (patch.email !== undefined) payload.email = patch.email
  if (patch.role !== undefined) payload.role = patch.role
  if (patch.employeeNumber !== undefined) payload.employee_number = patch.employeeNumber
  if (patch.sectorId !== undefined) payload.sector_id = patch.sectorId
  if (patch.shiftId !== undefined) payload.shift_id = patch.shiftId
  if (patch.locationId !== undefined) payload.location_id = patch.locationId
  if (patch.active !== undefined) payload.active = patch.active
  return payload
}

export async function updateEmployee(id: string, patch: Partial<EmployeeInput>): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const employee = database.profiles.find((item) => item.id === id)
    if (!employee) throw new Error('Empleado no encontrado.')
    if (patch.firstName !== undefined) employee.firstName = patch.firstName
    if (patch.lastName !== undefined) employee.lastName = patch.lastName
    if (patch.username !== undefined) employee.username = patch.username
    if (patch.email !== undefined) employee.email = patch.email ?? null
    if (patch.employeeNumber !== undefined) employee.employeeNumber = patch.employeeNumber ?? null
    if (patch.sectorId !== undefined) employee.sectorId = patch.sectorId ?? null
    if (patch.shiftId !== undefined) employee.shiftId = patch.shiftId ?? null
    if (patch.locationId !== undefined) employee.locationId = patch.locationId ?? null
    if (patch.active !== undefined) employee.active = patch.active
    if (patch.restrictions) employee.restrictions = patch.restrictions.map((restriction) => ({ id: newId(), employeeId: id, ...restriction }))
    employee.updatedAt = isoNow()
    commitDemo(database)
    return
  }
  const client = requireSupabase()
  const { error } = await client.from('profiles').update(profilePayload(patch)).eq('id', id)
  if (error) throw error
  if (patch.restrictions) {
    const { error: removeError } = await client.from('employee_restrictions').delete().eq('employee_id', id)
    if (removeError) throw removeError
    if (patch.restrictions.length) {
      const { error: insertError } = await client.from('employee_restrictions').insert(patch.restrictions.map((item) => ({ employee_id: id, type: item.type, notes: item.notes })))
      if (insertError) throw insertError
    }
  }
}

export async function updateOwnProfile(id: string, patch: Partial<Pick<AppProfile, 'firstName' | 'lastName' | 'email' | 'avatarUrl'>>): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const profile = database.profiles.find((item) => item.id === id)
    if (!profile) throw new Error('Perfil no encontrado.')
    const currentPassword = database.credentials[profile.username.toLowerCase()]
    if (patch.firstName !== undefined) profile.firstName = patch.firstName
    if (patch.lastName !== undefined) profile.lastName = patch.lastName
    if (patch.email !== undefined) {
      profile.email = patch.email
      if (patch.email && currentPassword) database.credentials[patch.email.toLowerCase()] = currentPassword
    }
    if (patch.avatarUrl !== undefined) profile.avatarUrl = patch.avatarUrl
    profile.updatedAt = isoNow()
    commitDemo(database)
    return
  }
  const payload: Record<string, unknown> = {}
  if (patch.firstName !== undefined) payload.first_name = patch.firstName
  if (patch.lastName !== undefined) payload.last_name = patch.lastName
  if (patch.email !== undefined) payload.email = patch.email
  if (patch.avatarUrl !== undefined) payload.avatar_url = patch.avatarUrl
  const { error } = await requireSupabase().from('profiles').update(payload).eq('id', id)
  if (error) throw error
}

export async function toggleEmployee(id: string): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const employee = database.profiles.find((item) => item.id === id)
    if (!employee) throw new Error('Empleado no encontrado.')
    employee.active = !employee.active
    employee.updatedAt = isoNow()
    commitDemo(database)
    return
  }
  const client = requireSupabase()
  const { data, error } = await client.from('profiles').select('active').eq('id', id).single()
  if (error) throw error
  const { error: updateError } = await client.from('profiles').update({ active: !booleanValue(record(data).active) }).eq('id', id)
  if (updateError) throw updateError
}

export async function deleteEmployee(id: string): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    if (database.selections.some((selection) => selection.employeeId === id)) throw new Error('El empleado tiene historial. Desactivalo en lugar de eliminarlo.')
    database.profiles = database.profiles.filter((profile) => profile.id !== id)
    commitDemo(database)
    return
  }
  const { error } = await requireSupabase().functions.invoke('admin-manage-user', { body: { action: 'delete', userId: id } })
  if (error) throw error
}

export async function resetEmployeePassword(id: string, password: string): Promise<void> {
  if (password.length < 4) throw new Error('La contraseña o PIN debe tener al menos 4 caracteres.')
  if (isDemoMode) {
    const database = getDemo()
    const employee = database.profiles.find((item) => item.id === id)
    if (!employee) throw new Error('Empleado no encontrado.')
    database.credentials[employee.username.toLowerCase()] = password
    if (employee.email) database.credentials[employee.email.toLowerCase()] = password
    commitDemo(database)
    return
  }
  const { error } = await requireSupabase().functions.invoke('admin-manage-user', { body: { action: 'password', userId: id, password } })
  if (error) throw error
}

export async function saveSelection(input: SelectionInput, isAdmin = false, actorId?: string): Promise<void> {
  const clearingSelection = !input.noMeal && !input.mealId
  if (clearingSelection && !isAdmin) throw new Error('Elegí un plato o la opción sin vianda.')
  if (input.noMeal && input.mealId) throw new Error('Elegí un plato o la opción sin vianda.')
  if (isDemoMode) {
    const database = getDemo()
    const week = database.weeks.find((item) => item.id === input.weekId)
    const day = week?.days.find((item) => item.id === input.weekDayId)
    if (!week || !day) throw new Error('Período o día no encontrado.')
    if (!isAdmin && (week.status !== 'open' || deadlinePassed(week))) throw new Error('deadline closed')
    if (isAdmin && week.status === 'finished') throw new Error('El período finalizado debe habilitarse antes de editar elecciones.')
    const existing = database.selections.find((selection) => selection.employeeId === input.employeeId && selection.weekDayId === input.weekDayId)
    if (clearingSelection) {
      database.selections = database.selections.filter((selection) => selection !== existing)
      demoAudit(database, actorId ?? null, 'selection.admin_cleared', 'selection', existing?.id ?? null, { employeeId: input.employeeId, weekDayId: input.weekDayId })
      commitDemo(database)
      return
    }
    if (!day.serviceEnabled) throw new Error('Ese día no tiene servicio de viandas.')
    if (existing) {
      existing.mealId = input.mealId
      existing.noMeal = input.noMeal
      existing.confirmed = input.confirmed ?? false
      existing.updatedAt = isoNow()
      if (isAdmin) {
        existing.modifiedByAdmin = actorId ?? null
        existing.modifiedAt = isoNow()
      }
    } else {
      database.selections.push({
        id: newId(),
        employeeId: input.employeeId,
        weekId: input.weekId,
        weekDayId: input.weekDayId,
        mealId: input.mealId,
        noMeal: input.noMeal,
        confirmed: input.confirmed ?? false,
        modifiedByAdmin: isAdmin ? actorId ?? null : null,
        modifiedAt: isAdmin ? isoNow() : null,
        createdAt: isoNow(),
        updatedAt: isoNow(),
      })
    }
    const employee = database.profiles.find((profile) => profile.id === input.employeeId)
    if (employee) employee.lastSelectionAt = isoNow()
    if (isAdmin) demoAudit(database, actorId ?? null, 'selection.admin_updated', 'selection', existing?.id ?? null, { employeeId: input.employeeId, weekDayId: input.weekDayId })
    commitDemo(database)
    return
  }
  const client = requireSupabase()
  if (clearingSelection) {
    const { error } = await client
      .from('selections')
      .delete()
      .eq('employee_id', input.employeeId)
      .eq('week_day_id', input.weekDayId)
    if (error) throw error
    return
  }
  const payload = {
    employee_id: input.employeeId,
    week_id: input.weekId,
    week_day_id: input.weekDayId,
    meal_id: input.mealId,
    no_meal: input.noMeal,
    confirmed: input.confirmed ?? false,
    ...(isAdmin ? { modified_by_admin: actorId, modified_at: isoNow() } : {}),
  }
  const { error } = await client.from('selections').upsert(payload, { onConflict: 'employee_id,week_day_id' })
  if (error) throw error
}

export async function confirmSelections(weekId: string, employeeId: string, isAdmin = false): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    const week = database.weeks.find((item) => item.id === weekId)
    if (!week || (!isAdmin && (week.status !== 'open' || deadlinePassed(week))) || week.status === 'finished') throw new Error('deadline closed')
    const requiredDays = week.days.filter((day) => day.serviceEnabled)
    const selected = database.selections.filter((selection) => selection.weekId === weekId && selection.employeeId === employeeId)
    if (!requiredDays.every((day) => selected.some((selection) => selection.weekDayId === day.id))) throw new Error('Completá todos los días antes de confirmar.')
    selected.forEach((selection) => { selection.confirmed = true; selection.updatedAt = isoNow() })
    commitDemo(database)
    return
  }
  const { error } = await requireSupabase().rpc('confirm_week_selections', { target_week_id: weekId, target_employee_id: employeeId })
  if (error) throw error
}

export async function updateSettings(patch: Partial<SystemSettings>): Promise<void> {
  if (isDemoMode) {
    const database = getDemo()
    database.settings = { ...database.settings, ...patch }
    commitDemo(database)
    return
  }
  const payload: Record<string, unknown> = { id: 'default' }
  if (patch.companyName !== undefined) payload.company_name = patch.companyName
  if (patch.systemName !== undefined) payload.system_name = patch.systemName
  if (patch.welcomeText !== undefined) payload.welcome_text = patch.welcomeText
  if (patch.logoUrl !== undefined) payload.logo_url = patch.logoUrl
  if (patch.serviceWeekdays !== undefined) payload.service_weekdays = patch.serviceWeekdays
  if (patch.timezone !== undefined) payload.timezone = patch.timezone
  const { error } = await requireSupabase().from('system_settings').upsert(payload)
  if (error) throw error
}

export async function fetchProfileById(id: string): Promise<AppProfile | null> {
  if (isDemoMode) return structuredClone(getDemo().profiles.find((profile) => profile.id === id) ?? null)
  const { data, error } = await requireSupabase().from('profiles').select('*, employee_restrictions(*)').eq('id', id).maybeSingle()
  if (error) throw error
  return data ? mapProfile(data) : null
}

export async function resolveLoginEmail(identifier: string): Promise<string> {
  const { data, error } = await requireSupabase().rpc('resolve_login_email', { login_identifier: identifier.trim().toLowerCase() })
  if (error || typeof data !== 'string') throw error ?? new Error('invalid login credentials')
  return data
}

export async function verifyDemoCredentials(identifier: string, password: string): Promise<AppProfile> {
  const database = getDemo()
  const normalized = identifier.trim().toLowerCase()
  if (database.credentials[normalized] !== password) throw new Error('invalid login credentials')
  const profile = database.profiles.find((item) => item.username.toLowerCase() === normalized || item.email?.toLowerCase() === normalized)
  if (!profile) throw new Error('invalid login credentials')
  if (!profile.active) throw new Error('inactive disabled')
  return structuredClone(profile)
}

export function dateInputToUtc(localDateTime: string): string {
  return fromZonedTime(localDateTime, APP_TIMEZONE).toISOString()
}

export function utcToDateInput(isoValue: string): string {
  return formatInTimeZone(parseISO(isoValue), APP_TIMEZONE, "yyyy-MM-dd'T'HH:mm")
}
